"use client";

import { useEffect, useRef } from "react";
import { MESSAGES_PERSIST_CAP, STORAGE_KEY_PREFIX } from "../_logic/constants";
import { PersistedShape, ThreadState } from "../_logic/types";

const DEBOUNCE_MS = 300;

const storageKey = (userId: number) => `${STORAGE_KEY_PREFIX}${userId}`;

/**
 * Reads persisted thread state for the given user.
 *
 * @param userId - Current user's `user_id`.
 * @returns Persisted state, or null if absent / malformed / wrong version.
 */
export const readPersistedState = (userId: number | null | undefined): PersistedShape | null => {
  if (typeof window === "undefined" || userId == null) return null;
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.version !== 1) return null;
    return parsed as PersistedShape;
  } catch {
    return null;
  }
};

/**
 * Removes persisted thread state for the given user.
 * Imperative so it can be called from auth-state handlers outside React.
 *
 * @param userId - User's `user_id` whose persisted thread should be cleared.
 */
export const clearPersistedState = (userId: number | null | undefined) => {
  if (typeof window === "undefined" || userId == null) return;
  window.localStorage.removeItem(storageKey(userId));
};

interface UseSupportChatPersistenceArgs {
  userId: number | null | undefined;
  thread: ThreadState;
}

/**
 * Persists support chat state to localStorage with a trailing 300ms debounce,
 * plus a `beforeunload` synchronous flush so a quick tab close after a send
 * doesn't lose state. Caps stored messages at `MESSAGES_PERSIST_CAP`.
 *
 * @param args - User id and the current thread state to persist.
 */
export const useSupportChatPersistence = ({ userId, thread }: UseSupportChatPersistenceArgs) => {
  const { messages, threadStatus, readThrough } = thread;
  const latestStateRef = useRef<PersistedShape>({ version: 1, messages, threadStatus, readThrough });
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const userIdRef = useRef(userId);

  // Keep userId current so the beforeunload listener (attached once) flushes
  // to the right key.
  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  // Update latest snapshot AND schedule a debounced write on each state change.
  useEffect(() => {
    const cappedMessages = messages.length > MESSAGES_PERSIST_CAP ? messages.slice(-MESSAGES_PERSIST_CAP) : messages;
    latestStateRef.current = { version: 1, messages: cappedMessages, threadStatus, readThrough };

    if (typeof window === "undefined" || userId == null) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      try {
        window.localStorage.setItem(storageKey(userId), JSON.stringify(latestStateRef.current));
      } catch {
        // Quota exceeded or storage disabled — silently drop.
      }
    }, DEBOUNCE_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [messages, threadStatus, readThrough, userId]);

  // Synchronous flush on tab close (`beforeunload`) and on mobile background
  // (`visibilitychange === "hidden"`). iOS Safari often skips beforeunload on
  // swipe-away/tab-switch, so visibilitychange is the reliable signal there.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const flush = () => {
      const id = userIdRef.current;
      if (id == null) return;
      try {
        window.localStorage.setItem(storageKey(id), JSON.stringify(latestStateRef.current));
      } catch {
        // ignore
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") flush();
    };
    window.addEventListener("beforeunload", flush);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("beforeunload", flush);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);
};
